import { useRollbar } from '@rollbar/react';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '../../components/Button';
import { Spacer } from '../../components/Spacer';
import { Typography } from '../../components/Typography';
import { PageLayout } from '../../layouts/PageLayout';
import { WidgetLayout } from '../../layouts/WidgetLayout';
import { devLog } from '../../utils/devLog';

const ErrorDisplay = ({ error, isEventError, reset }) => {
  const rollbar = useRollbar();

  useEffect(() => {
    // Check for errors that are likely false positives and should not be logged to rollbar
    // 1. Restaurant retrieval errors - mostly from bots/spam trying invalid venue slugs
    // 2. Generic API errors with [object Object] messages - often network/temporary/wrong config issues
    const message = String(error ?? '');
    const isFalsePositiveError =
      message.includes('an error occurred while retrieving the restaurant.') ||
      message.includes('[object Object]');

    if (!isFalsePositiveError) {
      rollbar.error(error);
    }
  }, [error]);

  devLog('error', 'Oh No!', error);

  return (
    <>
      <Helmet>
        <meta name='robots' content='noindex, nofollow' />
      </Helmet>
      <PageLayout>
        <WidgetLayout>
          <Spacer direction='vertical' gap='m' centered>
            {/* icon */}
            <Typography centered variant='h2'>
              Whoops, something went wrong.
            </Typography>
            <Typography centered>
              {isEventError
                ? 'We cannot find an event with the provided URL. Please contact the venue, or click the button below to see our availability.'
                : 'Please either refresh the page or contact the restaurant if there is an issue.'}
            </Typography>
            {isEventError && <Button onClick={reset}>Make a booking</Button>}
          </Spacer>
        </WidgetLayout>
      </PageLayout>
    </>
  );
};

ErrorDisplay.defaultProps = {
  isEventError: false,
  reset: () => {},
};

ErrorDisplay.propTypes = {
  error: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]).isRequired,
  isEventError: PropTypes.bool,
  reset: PropTypes.func,
};

export default ErrorDisplay;
